import { PackageType } from "@b2bportal/core-packages";
import { useModuleBEM } from "@b2bportal/core-themes";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import type { TFunction } from "i18next";
import styles from "./PackagePriceText.module.scss";

interface PackagePriceTextProps {
  showPointsPricing: boolean;
  hasTenantSpecific?: boolean;
  unitPriceRewards: string;
  unitPrice: string;
  totalPrice: string;
  packageType: PackageType;
  unavailableText?: string;
}

export const PackagePriceText = ({
  showPointsPricing,
  hasTenantSpecific,
  unitPriceRewards,
  unitPrice,
  totalPrice,
  packageType,
  unavailableText,
}: PackagePriceTextProps) => {
  const [block, cn] = useModuleBEM(styles, "PackagePriceText");
  const { t } = useI18nContext();

  if (!totalPrice || !unitPrice) {
    return (
      <div className={clsx(block, cn("price-container"))}>
        {unavailableText || t("core-packages.hotelCardPrice.notAvailable")}
      </div>
    );
  }

  const perPassengerPriceText = showPointsPricing ? (
    hasTenantSpecific ? (
      <Trans
        i18nKey="core-packages.priceWithPointsPlusPerPassenger"
        values={{ points: unitPriceRewards, price: unitPrice }}
        components={[<span className={cn("bold")} />]}
      />
    ) : (
      <Trans
        i18nKey="core-packages.priceWithPointsPerPassenger"
        values={{ points: unitPriceRewards, price: unitPrice }}
        components={[<span className={cn("bold")} />]}
      />
    )
  ) : (
    <Trans
      i18nKey="core-packages.pricePerPassenger"
      values={{ price: unitPrice }}
      components={[<span className={cn("bold")} />]}
    />
  );

  return (
    <div className={clsx(block, cn("price-container"))}>
      <span className={cn("price-per-passenger")}>{perPassengerPriceText}</span>
      <span className={cn("taxes-and-fees")}>
        {getPriceSubtitle(packageType, t)}
      </span>
      <span className={cn("total-price")}>{totalPrice}</span>
    </div>
  );
};

const getPriceSubtitle = (packageType: PackageType, t: TFunction) => {
  switch (packageType) {
    case PackageType.HOTEL_FLIGHT_CAR:
      return t("core-packages.priceSummary.whatsIncluded.hotelFlightCar");
    case PackageType.HOTEL_FLIGHT:
      return t("core-packages.priceSummary.whatsIncluded.hotelFlight");
    case PackageType.HOTEL_CAR:
      return t("core-packages.priceSummary.whatsIncluded.hotelCar");
    case PackageType.FLIGHT_CAR:
      return t("core-packages.priceSummary.whatsIncluded.flightCar");
    default:
      ensureExhaustive(packageType);
  }
};
